import {
  IMPACT_REPORT_DATE_PRESET_LABELS,
  IMPACT_REPORT_DATE_PRESET_PHRASES,
  IMPACT_REPORT_DEFAULT_DATE_PRESET,
  IMPACT_REPORT_LAST_30_DAYS_SPAN,
  IMPACT_REPORT_LAST_90_DAYS_SPAN,
  IMPACT_REPORT_MAX_FILTER_IDS,
} from "@/constants/impactReport";
import type {
  CappedImpactCampaignSelection,
  CauseCategory,
  ImpactDateSelection,
  ImpactDateWindow,
  ImpactEmptySelectionAction,
  ImpactFilterResolutionContext,
  ImpactReportControls,
  ReportFilter,
} from "@/interfaces/impactReport";
import { formatDateLong } from "@/utils/date-format";

/* ─────────────────────────────────────────────────────────────────────────────────────────────
   THE FILTER MODEL — the user's selections in, a `ReportFilter` out.

   THE ONE PLACE THIS APP COMPOSES A `ReportFilter`. No component builds one inline, because the
   traps here are not visible at a call site: the empty-vs-absent distinction, verbatim cause
   strings, a canonical ordering the query cache depends on, and a cap that must be legible rather
   than silent. Settling them once, in a pure module, is what keeps them settled.

   Pure by contract: no React, no DOM, no fetching, no randomness, and no clock — the clock and the
   browser's time zone arrive as an argument (`ImpactFilterResolutionContext`), so the same
   selections always compose the same filter and therefore hash to the same react-query key.
   `resolveImpactFilterContext` below is the single function that touches the environment.

   TWO THINGS THIS MODULE DELIBERATELY DOES NOT DO:

   1. It does not know about the view mode. Hero and Card are two renderings of ONE payload (both
      statement registers ship on every metric precisely so the switch costs no refetch), so
      `buildImpactReportFilter` has no parameter for it. A later reader cannot feed the view into
      a query key through this module, because there is no door.

   2. It does not GATE the request. `buildImpactReportFilter` is total: given no selections it
      cheerfully returns `{}` and never throws, guards, falls back or synthesises a scope field it
      was not given. For an organization user an empty filter means the whole organization (the
      upstream derives its campaigns from the caller); for GFG staff, who belong to no single
      organization, the upstream still answers 400 INVALID_FILTER, so their query stays disabled
      until they choose a campaign. That gate lives on the query's `enabled` flag;
      `canRequestImpactReport` below is the predicate it reads. Composing and gating are two
      different jobs, and folding the gate into the composer would leave the composer unable to express the empty state the control bar
      genuinely occupies before the user has chosen anything.

   NEVER EMITTED, from here or anywhere: `organizationId`. It is IDENTITY, resolved server-side
   from the authenticated caller and ignored in a query string. The filter stays about what is
   asked, never who asks — and it is not even a `ReportFilter` key, which is how it should stay.
   ───────────────────────────────────────────────────────────────────────────────────────────── */

/**
 * The single environment read in this module: the current instant and the browser's resolved IANA
 * time zone. Split out so every function below can stay deterministic — pass a fixed context and
 * the whole composition is reproducible.
 *
 * A runtime with no resolvable zone yields `""`, which the composer reads as "omit the key"
 * rather than sending an empty `timeZone` param.
 */
export function resolveImpactFilterContext(): ImpactFilterResolutionContext {
  return {
    now: new Date(),
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? "",
  };
}

/**
 * The selections a cleared control bar returns to: no causes, no campaigns, the default date
 * preset. Every call returns FRESH arrays — a shared frozen constant would be one object handed to
 * every consumer's state, and one accidental in-place `push` from bleeding across the app.
 *
 * It takes no argument, and specifically not the view mode: clearing filters is not a request to
 * change how the report is drawn, so this function is structurally incapable of touching it.
 * Doubles as the control bar's initial state — "cleared" and "untouched" are the same selections.
 */
export function clearImpactReportControls(): ImpactReportControls {
  return {
    cause: [],
    campaignIds: [],
    date: { preset: IMPACT_REPORT_DEFAULT_DATE_PRESET },
  };
}

/* ── Ordering ────────────────────────────────────────────────────────────────────────────────── */

/**
 * Ordering by UTF-16 code unit, NOT by locale.
 *
 * `localeCompare` collates differently across runtimes and ICU builds, so two browsers could order
 * one identical selection two ways and hash it to two different react-query keys — the same
 * request issued twice and cached twice. Code-unit order is the same everywhere, and this order is
 * never shown to anyone: it exists solely to make the key deterministic.
 */
function compareByCodeUnits(valueA: string, valueB: string): number {
  if (valueA < valueB) return -1;
  if (valueA > valueB) return 1;
  return 0;
}

/**
 * Distinct members in canonical order. Set membership is what a filter asserts, so a repeated id
 * says nothing new — and dropping the repeat keeps `["a", "a"]` and `["a"]`, which ask the exact
 * same question, from hashing to two different query keys.
 *
 * ORDERING AN ARRAY IS NOT MODIFYING ITS MEMBERS. The strings pass through untouched: never
 * lowercased, trimmed, slugified or otherwise normalised. Cause values are matched VERBATIM
 * against the upstream's stored reference values, there is no slug field anywhere in the contract,
 * and slugifying is lossy.
 */
function toCanonicalStringSelection(values: readonly string[]): string[] {
  return [...new Set(values)].sort(compareByCodeUnits);
}

/* ── The campaign cap ────────────────────────────────────────────────────────────────────────── */

/**
 * A campaign selection reduced to what one request may legally carry — with the reduction STATED,
 * never performed in silence.
 *
 * The upstream rejects more than `IMPACT_REPORT_MAX_FILTER_IDS` ids per field with a 400, so the
 * list has to be bounded before it is sent. What must not happen is bounding it quietly: a report
 * that answers "your first 250 campaigns" while presenting itself as the client's complete impact
 * is a WRONG ANSWER, not a limitation. `wasCapped`, `limit` and `selectedCount` exist so the
 * control bar can say so in words; this function's whole job is to make the cap legible.
 *
 * Capping happens AFTER canonical ordering so the retained ids are the same ids every time and on
 * every machine — the user is told a stable "250 of 312", not a different 250 per render.
 *
 * `buildImpactReportFilter` calls this, so nothing can send an over-long list by composing a
 * filter directly; any surface that renders a campaign selection must call it too and surface
 * `wasCapped`.
 */
export function capImpactCampaignSelection(
  campaignIds: readonly string[],
): CappedImpactCampaignSelection {
  const distinctCampaignIds = toCanonicalStringSelection(campaignIds);
  const wasCapped = distinctCampaignIds.length > IMPACT_REPORT_MAX_FILTER_IDS;

  if (wasCapped) {
    // Loud by design. A narrowing the user did not ask for is exactly the kind of change that is
    // invisible on screen unless something says it out loud, and the console entry survives after
    // the notice has been dismissed.
    console.error(
      `capImpactCampaignSelection: ${distinctCampaignIds.length} campaigns selected exceeds the ${IMPACT_REPORT_MAX_FILTER_IDS}-id filter limit; the request will carry the first ${IMPACT_REPORT_MAX_FILTER_IDS} in canonical id order and the UI MUST tell the user the report is narrowed`,
    );
  }

  return {
    campaignIds: distinctCampaignIds.slice(0, IMPACT_REPORT_MAX_FILTER_IDS),
    wasCapped,
    limit: IMPACT_REPORT_MAX_FILTER_IDS,
    selectedCount: distinctCampaignIds.length,
  };
}

/* ── The date selection ──────────────────────────────────────────────────────────────────────── */

/**
 * A `Date` as the bare `YYYY-MM-DD` the contract accepts, read from its LOCAL calendar fields.
 *
 * Local, not `toISOString()`: the composed filter travels with the browser's own `timeZone`, so
 * the bounds must be the dates the user sees on their own calendar. `toISOString()` would shift
 * "today" to yesterday for every user west of UTC after their local evening.
 */
function formatCalendarDate(date: Date): string {
  const year = String(date.getFullYear()).padStart(4, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * `date` shifted by whole calendar days, constructed from local components so a daylight-saving
 * transition inside the span cannot move the result onto the wrong date (subtracting 30 × 86.4e6
 * milliseconds across a spring-forward lands an hour short and rolls back a day).
 */
function shiftCalendarDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

/**
 * A custom bound as supplied, or `null` for "not supplied". `""` (a cleared date input) and
 * `undefined` (never touched) are the same state.
 *
 * The string is NOT validated or reformatted here. `<input type="date">` yields bare `YYYY-MM-DD`
 * already, and a malformed bound that somehow reaches this point belongs in front of the API's own
 * validator, which rejects it with a message naming the field — far better than this module
 * quietly dropping the user's date and answering a wider question than they asked.
 */
function readCustomBound(bound: string | undefined): string | null {
  return bound === undefined || bound === "" ? null : bound;
}

/**
 * Does this selection ask for a date window at all?
 *
 * The `custom` preset with only one bound filled answers NO — an incomplete range resolves to no
 * window rather than a half-open one, because a window open at one end silently asks a far wider
 * question than the user was in the middle of asking.
 *
 * `resolveImpactDateWindow`, `hasActiveImpactFilters` and `resolveImpactEmptySelectionAction` all
 * delegate here rather than each re-deciding it, so "the filter carries dates" and "the user has
 * narrowed by date" can never disagree.
 */
function dateSelectionRequestsWindow(selection: ImpactDateSelection): boolean {
  switch (selection.preset) {
    case "allTime":
      return false;
    case "last30Days":
    case "last90Days":
    case "thisYear":
    case "lastYear":
      return true;
    case "custom":
      return (
        readCustomBound(selection.customFrom) !== null &&
        readCustomBound(selection.customTo) !== null
      );
    default: {
      // Exhaustive at compile time: adding a preset fails to build until it is answered here.
      // Reported rather than thrown — an unrecognised preset must not take the page down, and
      // "no window" is the only safe reading, since inventing one would ask a question the user
      // never asked.
      const unhandledPreset: never = selection.preset;
      console.error(
        `dateSelectionRequestsWindow: unhandled date preset ${String(unhandledPreset)}; treated as "no date window"`,
      );
      return false;
    }
  }
}

/**
 * The date selection resolved to inclusive bare `YYYY-MM-DD` bounds, or `null` for NO WINDOW.
 *
 * `null` — "all time", and an incomplete custom range — means the composed filter carries no
 * `dateFrom`, no `dateTo` and no `timeZone` AT ALL. Not a very wide window: a window wide enough
 * to look like "everything" is a claim about when the account's giving began, and an absent window
 * is the absence of a claim. A `timeZone` with no window to interpret is meaningless, so it goes
 * with them.
 *
 * The spans mirror the prototype's `irDateRange`, with one deliberate difference: "last year" ends
 * on 31 December of last year rather than on 1 January of this one. The prototype compared
 * half-open millisecond instants; these bounds are INCLUSIVE calendar dates, so the prototype's
 * upper bound would pull this year's New Year's Day into "last year". The same inclusivity governs
 * the rolling presets: a SPAN-day window inclusive of both ends begins at `now - (SPAN - 1)`, not
 * at `now - SPAN`, because subtracting the whole span counts today twice — once as the endpoint and
 * again as a day of the window, so the report would claim SPAN + 1 days of giving. That is why the
 * shift is `SPAN - 1` while the constant keeps naming the window's true length.
 *
 * An inverted custom range (`customFrom` after `customTo`) is passed through exactly as selected.
 * It is a question with an empty answer, which is a truthful response; silently swapping the
 * bounds would answer a different question than the one on screen.
 */
export function resolveImpactDateWindow(
  selection: ImpactDateSelection,
  now: Date,
): ImpactDateWindow {
  if (!dateSelectionRequestsWindow(selection)) return null;

  switch (selection.preset) {
    case "last30Days":
      return {
        dateFrom: formatCalendarDate(
          shiftCalendarDays(now, -(IMPACT_REPORT_LAST_30_DAYS_SPAN - 1)),
        ),
        dateTo: formatCalendarDate(now),
      };
    case "last90Days":
      return {
        dateFrom: formatCalendarDate(
          shiftCalendarDays(now, -(IMPACT_REPORT_LAST_90_DAYS_SPAN - 1)),
        ),
        dateTo: formatCalendarDate(now),
      };
    case "thisYear":
      return {
        dateFrom: formatCalendarDate(new Date(now.getFullYear(), 0, 1)),
        dateTo: formatCalendarDate(now),
      };
    case "lastYear":
      return {
        dateFrom: formatCalendarDate(new Date(now.getFullYear() - 1, 0, 1)),
        dateTo: formatCalendarDate(new Date(now.getFullYear() - 1, 11, 31)),
      };
    case "custom": {
      const customFrom = readCustomBound(selection.customFrom);
      const customTo = readCustomBound(selection.customTo);
      // Unreachable: `dateSelectionRequestsWindow` already proved both bounds are present for a
      // custom selection. Re-read here only because the compiler cannot carry that proof across
      // the call, and answered with "no window" rather than a half-open one.
      if (customFrom === null || customTo === null) return null;
      return { dateFrom: customFrom, dateTo: customTo };
    }
    default:
      // `allTime` and any future preset are already answered by the guard above, which is the one
      // place the preset set is enumerated exhaustively.
      return null;
  }
}

/**
 * The DATE pill's label for the REQUESTED window, or `null` when the selection requests none
 * (`allTime`, or an incomplete custom range — neither is an applied filter, so neither gets a
 * pill). A custom range reads as its two bounds; every other preset as its menu label.
 */
export function describeImpactDateSelection(
  selection: ImpactDateSelection,
  now: Date,
): string | null {
  const dateWindow = resolveImpactDateWindow(selection, now);
  if (dateWindow === null) return null;

  return selection.preset === "custom"
    ? `${formatDateLong(dateWindow.dateFrom)} – ${formatDateLong(dateWindow.dateTo)}`
    : IMPACT_REPORT_DATE_PRESET_LABELS[selection.preset];
}

/**
 * The REQUESTED window as a sentence fragment for the empty-state message ("in the last 30 days",
 * "between 1 March 2026 and 31 March 2026"), or `null` when the selection requests none. The
 * sentence counterpart of `describeImpactDateSelection`, and null in exactly the same cases.
 */
export function describeImpactDatePhrase(selection: ImpactDateSelection, now: Date): string | null {
  const dateWindow = resolveImpactDateWindow(selection, now);
  if (dateWindow === null) return null;

  switch (selection.preset) {
    case "custom":
      return `between ${formatDateLong(dateWindow.dateFrom)} and ${formatDateLong(dateWindow.dateTo)}`;
    case "allTime":
      // Unreachable: `allTime` resolves to no window above. Answered only so the compiler can
      // narrow the index below to the presets that have a phrase.
      return null;
    default:
      return IMPACT_REPORT_DATE_PRESET_PHRASES[selection.preset];
  }
}

/* ── The three predicates — different questions, never interchangeable ────────────────────────── */

/**
 * HAS THE USER NARROWED ANYTHING? True when the composed filter would carry at least one
 * server-affecting field — a cause, a campaign, or a resolvable date window.
 *
 * This is the PILLS question. It drives the active-filter pills and the clear-all affordance —
 * every field that would appear as a pill counts, campaign selection included. It is true for a
 * cause-only or date-only selection, neither of which carries a scope.
 *
 * NOT the same question as `canRequestImpactReport` below, and the two are never substitutable:
 * a cause-only selection is ACTIVE (pills show, clear-all appears) yet NOT REQUESTABLE (no scope
 * field, so the upstream would answer 400). Using this one for the query's `enabled` flag is
 * exactly the bug that would issue an unscoped request.
 */
export function hasActiveImpactFilters(controls: ImpactReportControls): boolean {
  return (
    controls.cause.length > 0 ||
    controls.campaignIds.length > 0 ||
    dateSelectionRequestsWindow(controls.date)
  );
}

/**
 * HAS THE READER NARROWED THE DOCUMENT TO A CHOSEN SET OF CAUSES? True exactly when one or more
 * causes are selected.
 *
 * This is the SUMMARY-PAGE question. The hero document's cover restates the account as a whole —
 * its headline, its main-cause line and its three figures — which earns its place on a
 * whole-account report and repeats the single page beneath it on a cause-filtered one. A document
 * narrowed to chosen causes therefore opens on the first cause page instead. It is also the
 * question the CAUSE menu's refresh rule asks, because a payload fetched under a cause filter
 * lists only the causes already selected.
 *
 * A DATE WINDOW DOES NOT COUNT. Counting one would drop the summary page from a date-filtered
 * whole-account report — the one report the summary page is most useful on, and a narrowing nobody
 * asked to change the document's shape. A date window narrows WHEN the giving happened; it does not
 * reduce the account to a single cause, so the page that describes the account as a whole still
 * has something to say.
 *
 * NOT a "strict subset of the available causes" test either, and that must not be attempted: a
 * selection covering every cause reads like an unfiltered report, but `causeOptions` is `[]` until
 * a payload has arrived — so `cause.length === causeOptions.length` is `0 === 0` on exactly the
 * fresh, unfiltered load that most needs its cover — and the option list deliberately stops
 * refreshing while a cause filter is applied, leaving it frozen precisely when the comparison
 * would be made. ANY cause selection at all answers YES.
 */
export function hasImpactCauseSelection(controls: ImpactReportControls): boolean {
  return controls.cause.length > 0;
}

/**
 * MAY THE REPORT BE REQUESTED AT ALL? True when the selection carries a campaign SCOPE, or when
 * the caller may ask for their whole organization (`allowOrgScope`).
 *
 * This is the REQUEST question, and it is what the query's `enabled` flag reads. An empty campaign
 * selection means "the caller's whole organization": the upstream derives that organization's
 * campaigns server-side from the authenticated caller, so the filter names no scope field and the
 * page opens on the all-time, all-campaigns report. GFG staff (`isGfg`) have no single
 * organization to derive, and the upstream answers 400 INVALID_FILTER to their empty scope — so
 * for them `allowOrgScope` is false and the page stays selection-first until a campaign is chosen.
 * Callers pass false while permissions are still loading, so nothing fires before the caller is
 * known.
 *
 * The cap never empties a non-empty selection, so this reads the raw selection rather than the
 * capped one — the two can only ever agree.
 *
 * NOT the same question as `hasActiveImpactFilters` above. Using that one here would enable the
 * query on a cause-only selection, which for GFG staff is still a request with no scope.
 */
export function canRequestImpactReport(
  controls: ImpactReportControls,
  { allowOrgScope }: { allowOrgScope: boolean },
): boolean {
  return controls.campaignIds.length > 0 || allowOrgScope;
}

/* ── The composition ─────────────────────────────────────────────────────────────────────────── */

/**
 * THE user's selections → THE `ReportFilter` that goes on the wire. The only function in this app
 * that produces one.
 *
 * TOTAL AND UNGUARDED. Given no selections it returns `{}` — it never throws, never refuses, never
 * falls back and never synthesises a scope field the user did not choose. Whether an empty filter
 * may be REQUESTED is a separate question with a separate answer (`canRequestImpactReport`), read
 * at the query's `enabled` flag; see this module's header for why the two are kept apart.
 *
 * A KEY IS OMITTED, NEVER EMITTED EMPTY. An empty array is a different assertion from an absent
 * field — the upstream constraint reads specifically "a NON-EMPTY `campaignIds`" — and the
 * serializer rejects a present-but-empty param outright. Omission also keeps the react-query key
 * from churning between "absent" and "empty", so two callers passing the same selection hash to
 * one key and share one cache entry instead of issuing the aggregate twice. No field is ever
 * assigned `undefined`, which would put the key in the object.
 *
 * NO VIEW-MODE PARAMETER, deliberately. See the module header.
 *
 * NEVER emits `organizationId`. See the module header.
 *
 * Fields the control bar does not expose — `orderIds`, `metric`, `vendor`, `sdgIds`, `minDisplay`,
 * `fallbackLanguage`, `includeRefunded`, `clientId` — stay unset, and the server's own defaults
 * apply (`minDisplay` defaults to `'enforce'`). In particular the display floor is the SERVER's
 * rule and is never reimplemented on this side.
 */
export function buildImpactReportFilter(
  controls: ImpactReportControls,
  context: ImpactFilterResolutionContext = resolveImpactFilterContext(),
): ReportFilter {
  const filter: ReportFilter = {};

  // Verbatim values, deduplicated and ordered — never re-cased, trimmed or slugified.
  const cause: CauseCategory[] = toCanonicalStringSelection(controls.cause);
  if (cause.length > 0) filter.cause = cause;

  const cappedSelection = capImpactCampaignSelection(controls.campaignIds);
  if (cappedSelection.campaignIds.length > 0) filter.campaignIds = cappedSelection.campaignIds;

  const dateWindow = resolveImpactDateWindow(controls.date, context.now);
  if (dateWindow !== null) {
    filter.dateFrom = dateWindow.dateFrom;
    filter.dateTo = dateWindow.dateTo;
    // Only ever alongside a window: a time zone with nothing to interpret is meaningless, and
    // sending it alone would put a key on the wire that answers no question.
    if (context.timeZone !== "") filter.timeZone = context.timeZone;
  }

  return filter;
}

/**
 * The ONE action an empty selection-scoped report suggests. Fixed precedence — date, then cause,
 * then campaigns — because the empty state offers a single button rather than a menu of fixes, and
 * the date window is the widest lever: dropping it lets every redemption the other selections
 * already cover back in, where clearing a cause only readmits that slice. With neither narrowing
 * applied, the campaign choice itself is what came back empty, so the user is sent to pick
 * campaigns that have redeemed gifts.
 */
export function resolveImpactEmptySelectionAction(
  controls: ImpactReportControls,
): ImpactEmptySelectionAction {
  if (dateSelectionRequestsWindow(controls.date)) return "widenDate";
  if (controls.cause.length > 0) return "clearCause";
  return "showCampaigns";
}

/**
 * The one selected campaign's id and display name, or `null` when zero or several are selected —
 * the caller decides what THOSE states look like, this only ever answers the single-selection one.
 *
 * Written as an early-return rather than `selectedCampaignIds[0]` at the call site: this repo's
 * `tsconfig` has `noUncheckedIndexedAccess` on, so an array index is `string | undefined` even
 * right after a `.length === 1` check, and narrowing it here — once — keeps every caller of this
 * function working with a plain `string`, never an assertion.
 */
export function findSingleSelectedCampaign(
  campaignIds: readonly string[],
  campaigns: ReadonlyArray<{ id: string; name: string }> | undefined,
): { id: string; name: string } | null {
  if (campaignIds.length !== 1) return null;

  const id = campaignIds[0];
  if (id === undefined) return null;

  // No matching campaign (still loading, or since deleted): fall back to the id rather than
  // inventing a name or hiding the pill — the selection is real even if the label is thin.
  const name = (campaigns ?? []).find((campaign) => campaign.id === id)?.name ?? id;
  return { id, name };
}
